import { useEffect, useMemo, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Icon } from '@iconify/react'
import Button from '../components/common/Button'
import LoadingSpinner from '../components/common/LoadingSpinner'
import { planService } from '../services/api/planService'

const formatPriceValue = (plan) => {
  const currency = plan?.currency || 'USD'
  const price = typeof plan?.price === 'number' ? plan.price : Number(plan?.price || 0)

  if (currency === 'USD') return `$${price}`
  return `${currency} ${price}`
}

const parseDescriptionToTagAndFeatures = (description) => {
  if (!description) return { tagline: '', features: [] }

  const parts = description
    .split(',')
    .map((part) => part.trim().replace(/\.$/, ''))
    .filter(Boolean)

  if (parts.length <= 1) return { tagline: description, features: [] }
  return { tagline: parts[0], features: parts.slice(1, 7) }
}

const Plans = () => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [plans, setPlans] = useState([])

  useEffect(() => {
    const loadPlans = async () => {
      setIsLoading(true)
      setError('')
      try {
        const data = await planService.list({ page: 1, limit: 10 })
        setPlans(data?.results || [])
      } catch (err) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load plans.'
        setError(errorMessage)
        toast.error(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    loadPlans()
  }, [])

  const defaultPlanId = useMemo(() => plans.find((p) => p.isDefault)?._id, [plans])

  const onSelectPlan = (plan) => {
    try {
      localStorage.setItem('selectedPlan', JSON.stringify(plan))
    } catch {
      // ignore storage errors
    }
    navigate(`/register?planId=${encodeURIComponent(plan._id)}`)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-white to-purple-50 p-4">
      <div className="max-w-5xl mx-auto py-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary to-primary-dark rounded-2xl mb-4 shadow-lg">
            <Icon icon="mdi:shield-check" className="text-3xl text-white" />
          </div>
          <h1 className="text-3xl font-bold text-text-dark mb-2">Choose your plan</h1>
          <p className="text-text-secondary">Select a plan to continue to registration.</p>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <LoadingSpinner />
          </div>
        )}

        {!isLoading && error && (
          <div className="mb-6 p-4 bg-danger/10 border border-danger/20 rounded-xl text-danger">
            {error}
          </div>
        )}

        {!isLoading && !error && plans.length === 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-border-light text-center">
            <p className="text-text-secondary">No plans available right now.</p>
          </div>
        )}

        {!isLoading && plans.length > 0 && (
          <div>
            <section aria-label="Pricing plans" className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-8">
              {plans.map((plan) => {
                const isFeatured = plan._id === defaultPlanId
                const { tagline, features } = parseDescriptionToTagAndFeatures(plan.description)
                return (
                  <div
                    key={plan._id}
                    className={[
                      'relative bg-white rounded-2xl border shadow-sm p-5 md:p-7 flex flex-col',
                      isFeatured ? 'border-primary shadow-md' : 'border-border-light',
                    ].join(' ')}
                  >
                    {isFeatured && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="px-4 py-1 rounded-full bg-primary text-white text-xs font-semibold shadow-sm">
                          Most Popular
                        </span>
                      </div>
                    )}

                  <div className="mb-4">
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg md:text-xl font-semibold text-text-dark">{plan.name}</h2>
                      {isFeatured && (
                        <Icon
                          icon="mdi:lightning-bolt-outline"
                          className="text-primary text-lg"
                          aria-hidden="true"
                        />
                      )}
                    </div>
                    <p className="text-sm text-text-secondary mt-1">{tagline || plan.description}</p>
                  </div>

                  <div className="mb-5">
                    <p className="text-4xl font-extrabold text-text-dark tracking-tight">
                      {formatPriceValue(plan)}
                      <span className="text-base font-semibold text-text-secondary">
                        {plan?.billingCycle ? `/${plan.billingCycle}` : ''}
                      </span>
                    </p>
                  </div>

                  {features.length > 0 && (
                    <ul className="space-y-3 mb-6">
                      {features.map((feature) => (
                        <li key={feature} className="flex items-start gap-3">
                          <Icon
                            icon="mdi:check-circle"
                            className="text-success text-xl mt-0.5"
                            aria-hidden="true"
                          />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Button
                    variant={isFeatured ? 'primary' : 'secondary'}
                    className="w-full mt-auto"
                    onClick={() => onSelectPlan(plan)}
                  >
                    Get Started
                  </Button>
                  </div>
                )
              })}
            </section>

            <p className="text-center text-sm text-text-secondary mb-6">
              All plans include money-back guarantee - Cancel anytime - No setup fees
            </p>
          </div>
        )}

        <div className="mt-8 text-center text-sm text-text-secondary">
          <Link to="/login" className="text-primary hover:text-primary-dark hover:bg-primary/20 p-3 rounded font-medium inline-flex items-center gap-2">
            <Icon icon="mdi:arrow-left" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  )
}

export default Plans
